import { KEPT_DB, KEPT_STORE, keepSave } from "./keptSaves";
import { displayName, romBaseName, stateSlotOf } from "./saves";
import { OWNER_KEY } from "./syncRecords";

// Games, saves and slots live in browser storage that every account on this
// browser shares, but they belong to one: the account in `localOwner`. When
// a different account signs in, the owner's saves are put aside, here, in
// their own database, and the new account's own (if it has played here
// before) are put back, so each account has its own saves, slots and sync
// state and cloud sync runs for whoever is signed in. The games themselves
// stay put.
//
// The owner is claimed at sign-in, not at the first sync: an account that
// played here and never synced (no group, offline) still owns what it
// played, and it never reaches the next account's cloud. Signed-out play is
// nobody's until someone signs in, and then theirs: a load signed out puts
// the last owner's saves aside and leaves the browser ownerless, so what is
// played next stays out of that stash, and the next account to sign in
// adopts it (where it has its own copy of a file, its own stays and the
// signed-out one goes aside: a state to a free slot, a battery save to the
// account's kept saves, keptSaves.ts).
//
// It only runs at page load, before an emulator core mounts its files, and
// only when no other tab of the app is open (a tab keeps a shared lock for
// as long as it lives): another tab would write the old owner's files back.
// Every step can be repeated, and the journal says where to resume after a
// crash.

const STASH_DB = "pocket-play-profiles";
const STASH_STORE = "records";
const LOCK = "pocket-play-profile";
const JOURNAL_KEY = "pocket-play.profileSwap";
const LOCAL_STORAGE = "localStorage";

// Per-account localStorage: slots, what cloud sync matched, guide progress.
// Not the owner marker, settings or games' systems, which belong to the
// browser (settings that follow the account come from the cloud at load:
// common/preferences.ts).
const LOCAL_PREFIXES = [
  "pocket-play.slot.",
  "pocket-play.slots.",
  "pocket-play.slotName.",
  "pocket-play.slotStats.",
  "pocket-play.cloudSync.",
  "pocket-play.guide.",
];

/** Where signed-out settings go when the account adopting them has its own; battery saves kept here before go to keptSaves. */
export const SIGNED_OUT = "signed-out";

interface Source {
  db: string;
  store: string;
  /** Key prefixes to take, or null for the whole store. */
  prefixes: string[] | null;
  /** Make the database when the browser has none (only ours, never an IDBFS one). */
  create: boolean;
}

// mGBA's saves and states, EmulatorJS's game saves, EmulatorJS's states, and
// the battery saves kept aside.
const SOURCES: Source[] = [
  { db: "/data", store: "FILE_DATA", prefixes: ["/data/saves/", "/data/states/"], create: false },
  { db: "/data/saves", store: "FILE_DATA", prefixes: ["/data/saves/"], create: false },
  { db: "pocket-play-ejs-states", store: "states", prefixes: null, create: true },
  { db: KEPT_DB, store: KEPT_STORE, prefixes: null, create: true },
];

interface Entry {
  key: string;
  value: unknown;
}

interface Journal {
  from: string;
  /** null: nobody, signed out. */
  to: string | null;
  phase: "stash" | "restore";
}

export type ProfileState = "ready" | "blocked";

const settled = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const finished = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error);
  });

// Opening a database that doesn't exist would make an empty one at version
// 1, which IDBFS (version 21) would then refuse to mount; aborting the
// upgrade makes nothing.
function openSource(source: Source): Promise<IDBDatabase | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(source.db);
    request.onupgradeneeded = () => {
      if (source.create) request.result.createObjectStore(source.store);
      else request.transaction?.abort();
    };
    request.onerror = () => (source.create ? reject(request.error) : resolve(null));
    request.onsuccess = () => {
      if (request.result.objectStoreNames.contains(source.store)) resolve(request.result);
      else {
        request.result.close();
        resolve(null);
      }
    };
  });
}

function openStash(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(STASH_DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STASH_STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// An IDBFS directory has no contents; keep it so a restored file's folder exists.
const isFile = (source: Source, record: Entry) =>
  source.store !== "FILE_DATA" || (record.value as { contents?: unknown } | undefined)?.contents !== undefined;

const ranges = (source: Source) =>
  source.prefixes ? source.prefixes.map((p) => IDBKeyRange.bound(p, `${p}￿`)) : [undefined];

async function readLive(source: Source): Promise<Entry[]> {
  const db = await openSource(source);
  if (!db) return [];
  try {
    const store = db.transaction(source.store, "readonly").objectStore(source.store);
    const found: Entry[] = [];
    for (const range of ranges(source)) {
      const keys = await settled(store.getAllKeys(range));
      const values = await settled(store.getAll(range));
      keys.forEach((key, i) => found.push({ key: String(key), value: values[i] }));
    }
    return found;
  } finally {
    db.close();
  }
}

async function removeLive(source: Source, records: Entry[]) {
  const db = await openSource(source);
  if (!db) return;
  try {
    const tx = db.transaction(source.store, "readwrite");
    for (const { key } of records) tx.objectStore(source.store).delete(key);
    await finished(tx);
  } finally {
    db.close();
  }
}

/** False when the browser has no such database to put them in. */
async function writeLive(source: Source, records: Entry[]): Promise<boolean> {
  const db = await openSource(source);
  if (!db) return false;
  try {
    const tx = db.transaction(source.store, "readwrite");
    const store = tx.objectStore(source.store);
    for (const record of records) {
      if (isFile(source, record)) store.put(record.value, record.key);
      else
        settled(store.getKey(record.key)).then((existing) => {
          if (existing === undefined) store.put(record.value, record.key);
        });
    }
    await finished(tx);
    return true;
  } finally {
    db.close();
  }
}

const stashRange = (sub: string) => IDBKeyRange.bound([sub], [sub, []]);

async function putStash(sub: string, db: string, records: Entry[]) {
  if (!records.length) return;
  const stash = await openStash();
  try {
    const tx = stash.transaction(STASH_STORE, "readwrite");
    for (const { key, value } of records) tx.objectStore(STASH_STORE).put({ db, key, value }, [sub, db, key]);
    await finished(tx);
  } finally {
    stash.close();
  }
}

async function readStash(sub: string): Promise<Map<string, Entry[]>> {
  const stash = await openStash();
  try {
    const rows: { db: string; key: string; value: unknown }[] = await settled(
      stash.transaction(STASH_STORE, "readonly").objectStore(STASH_STORE).getAll(stashRange(sub)),
    );
    const byDb = new Map<string, Entry[]>();
    for (const { db, key, value } of rows) byDb.set(db, [...(byDb.get(db) ?? []), { key, value }]);
    return byDb;
  } finally {
    stash.close();
  }
}

async function dropStash(sub: string, db: string) {
  const stash = await openStash();
  try {
    const tx = stash.transaction(STASH_STORE, "readwrite");
    const store = tx.objectStore(STASH_STORE);
    const range = IDBKeyRange.bound([sub, db], [sub, db, []]);
    store.delete(range);
    await finished(tx);
  } finally {
    stash.close();
  }
}

const liveLocal = (): Record<string, string> => {
  const found: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)!;
    if (LOCAL_PREFIXES.some((p) => key.startsWith(p))) found[key] = localStorage.getItem(key) ?? "";
  }
  return found;
};

/** Puts the account's files and settings aside, taking them out of the live stores. */
async function stashLive(sub: string) {
  for (const source of SOURCES) {
    const records = await readLive(source);
    await putStash(sub, source.db, records);
    await removeLive(
      source,
      records.filter((r) => isFile(source, r)),
    );
  }
  // Merged with what an interrupted earlier run already put aside.
  const live = liveLocal();
  const earlier = (await readStash(sub)).get(LOCAL_STORAGE)?.[0]?.value as Record<string, string> | undefined;
  await putStash(sub, LOCAL_STORAGE, [{ key: "all", value: { ...earlier, ...live } }]);
  Object.keys(live).forEach((key) => localStorage.removeItem(key));
}

/** Puts an account's saved-aside files and settings back, then forgets the copy. */
async function restore(sub: string) {
  const stashed = await readStash(sub);
  for (const source of SOURCES) {
    const records = stashed.get(source.db);
    if (records && (await writeLive(source, records))) await dropStash(sub, source.db);
  }
  const local = stashed.get(LOCAL_STORAGE)?.[0]?.value as Record<string, string> | undefined;
  for (const [key, value] of Object.entries(local ?? {})) localStorage.setItem(key, value);
  await dropStash(sub, LOCAL_STORAGE);
}

function readJournal(): Journal | null {
  try {
    return JSON.parse(localStorage.getItem(JOURNAL_KEY) ?? "null") as Journal | null;
  } catch {
    return null;
  }
}

const writeJournal = (journal: Journal) => localStorage.setItem(JOURNAL_KEY, JSON.stringify(journal));

/**
 * Hands the browser's saves from one account to another, or to nobody (null:
 * signed out, the browser left ownerless). Repeats safely from the journal.
 */
export async function swapProfile(from: string, to: string | null, resume: Journal["phase"] = "stash") {
  if (resume === "stash") {
    writeJournal({ from, to, phase: "stash" });
    await stashLive(from);
    writeJournal({ from, to, phase: "restore" });
  }
  if (to) {
    await restore(to);
    localStorage.setItem(OWNER_KEY, to);
  } else {
    localStorage.removeItem(OWNER_KEY);
  }
  localStorage.removeItem(JOURNAL_KEY);
}

// A state file's slot comes from its name (<base>.ssN, saves.ts), read up to
// three digits; slot 99 is useEmulator's scratch slot for a cheat reload.
export const MAX_SLOT = 999;
const SCRATCH_SLOT = 99;

/** What adopting signed-out saves did that the player should hear about (AdoptedSaves shows it). */
export const ADOPT_NOTICES_KEY = "pocket-play.adoptNotices";
/** An account's signed-out states that found every slot taken: placed at a later load, once one is free. */
const pendingProfile = (sub: string) => `pending:${sub}`;

const fileName = (key: string) => key.slice(key.lastIndexOf("/") + 1);
const gameLabel = (base: string) => displayName(`${base}.x`);
const isState = (source: Source, record: Entry) =>
  isFile(source, record) &&
  (source.store === "states" || record.key.startsWith("/data/states/")) &&
  stateSlotOf(fileName(record.key)) !== null;
const isBattery = (source: Source, record: Entry) => isFile(source, record) && record.key.startsWith("/data/saves/");
const idbfsFile = (record: Entry) => record.value as { contents: Uint8Array; timestamp?: Date | number };
const keepBattery = (record: Entry) => {
  const { contents, timestamp } = idbfsFile(record);
  return keepSave(
    fileName(record.key),
    contents,
    "signed-out",
    timestamp === undefined ? null : new Date(timestamp).getTime(),
  );
};

function notify(text: string) {
  let notices: string[] = [];
  try {
    notices = JSON.parse(localStorage.getItem(ADOPT_NOTICES_KEY) ?? "[]") as string[];
  } catch {
    /* start over */
  }
  localStorage.setItem(ADOPT_NOTICES_KEY, JSON.stringify([...notices, text]));
}

class Slots {
  private byBase = new Map<string, Set<number>>();
  add(base: string, slot: number) {
    this.byBase.set(base, (this.byBase.get(base) ?? new Set()).add(slot));
  }
  has(base: string, slot: number) {
    return !!this.byBase.get(base)?.has(slot);
  }
  remove(base: string, slot: number) {
    this.byBase.get(base)?.delete(slot);
  }
  addState(name: string) {
    const slot = stateSlotOf(name);
    if (slot !== null) this.add(romBaseName(name), slot);
  }
  addLists(local: Record<string, string>) {
    for (const [key, value] of Object.entries(local)) {
      if (!key.startsWith(SLOTS)) continue;
      try {
        for (const slot of JSON.parse(value) as number[]) this.add(romBaseName(key.slice(SLOTS.length)), slot);
      } catch {
        /* not a list */
      }
    }
  }
  free(base: string, maxSlot: number, also: Slots): number | null {
    for (let n = 1; n <= maxSlot; n++) if (n !== SCRATCH_SLOT && !this.has(base, n) && !also.has(base, n)) return n;
    return null;
  }
}

const SLOTS = "pocket-play.slots.";
const ACTIVE = "pocket-play.slot.";
const SLOT_META = ["pocket-play.slotName.", "pocket-play.slotStats."];

const romsOf = (base: string) =>
  Object.keys(localStorage)
    .filter((key) => key.startsWith(SLOTS) || key.startsWith(ACTIVE))
    .map((key) => key.slice(key.startsWith(SLOTS) ? SLOTS.length : ACTIVE.length))
    .filter((rom, i, all) => romBaseName(rom) === base && all.indexOf(rom) === i);

const readList = (key: string): number[] => {
  try {
    return JSON.parse(localStorage.getItem(key) ?? "[]") as number[];
  } catch {
    return [];
  }
};

/** Moves a game's slot bookkeeping (list entry, active slot, name, stats) from one slot to another, or drops it (null). */
function moveSlotMeta(base: string, from: number, to: number | null) {
  for (const rom of romsOf(base)) {
    const list = readList(SLOTS + rom).filter((n) => n !== from);
    if (localStorage.getItem(SLOTS + rom) !== null || to !== null)
      localStorage.setItem(SLOTS + rom, JSON.stringify((to === null ? list : [...list, to]).sort((a, b) => a - b)));
    if (localStorage.getItem(ACTIVE + rom) === String(from)) {
      if (to === null) localStorage.removeItem(ACTIVE + rom);
      else localStorage.setItem(ACTIVE + rom, String(to));
    }
    for (const prefix of SLOT_META) {
      const value = localStorage.getItem(`${prefix}${rom}.${from}`);
      if (value === null) continue;
      localStorage.removeItem(`${prefix}${rom}.${from}`);
      if (to !== null) localStorage.setItem(`${prefix}${rom}.${to}`, value);
    }
  }
}

const renamed = (key: string, slot: number) => {
  const name = fileName(key);
  return `${key.slice(0, key.length - name.length)}${romBaseName(name)}.ss${slot}`;
};

/**
 * Makes signed-out play the signing-in account's, with its own saves from
 * before put back, and never overwrites either side's save. A signed-out
 * state in a slot the account already has (here or in its cloud copy,
 * `cloudNames`) moves to the game's next free slot; with every slot taken it
 * waits, on this device and unsynced, for one to free up. A signed-out
 * battery save where the account has one goes to the account's kept saves,
 * on this device, for the library page to restore. The player is told each
 * time (ADOPT_NOTICES_KEY). Repeatable: the owner is set last.
 */
export async function adoptProfile(sub: string, cloudNames: Set<string>, maxSlot = MAX_SLOT) {
  const stashed = await readStash(sub);
  const theirLocal = (stashed.get(LOCAL_STORAGE)?.[0]?.value ?? {}) as Record<string, string>;
  const accounts = new Slots();
  for (const source of SOURCES)
    for (const r of stashed.get(source.db) ?? []) if (isState(source, r)) accounts.addState(fileName(r.key));
  for (const name of cloudNames) accounts.addState(name);
  accounts.addLists(theirLocal);

  const signedOut = new Slots();
  const live = await Promise.all(SOURCES.map(readLive));
  SOURCES.forEach((source, i) =>
    live[i].filter((r) => isState(source, r)).forEach((r) => signedOut.addState(fileName(r.key))),
  );

  for (const [i, source] of SOURCES.entries()) {
    for (const record of live[i]) {
      const name = fileName(record.key);
      const base = romBaseName(name);
      if (isState(source, record)) {
        const slot = stateSlotOf(name)!;
        if (!accounts.has(base, slot)) continue;
        const to = accounts.free(base, maxSlot, signedOut);
        if (to !== null) {
          await writeLive(source, [{ key: renamed(record.key, to), value: record.value }]);
          signedOut.add(base, to);
          notify(
            `${gameLabel(base)}: your state in slot ${slot} from before you signed in is now in slot ${to}, since this account already has a slot ${slot}.`,
          );
        } else {
          await putStash(pendingProfile(sub), source.db, [record]);
          notify(
            `${gameLabel(base)}: every slot is taken, so your state in slot ${slot} from before you signed in is kept on this device, unsynced. Free a slot and it moves there the next time CloudBoy opens.`,
          );
        }
        await removeLive(source, [record]);
        signedOut.remove(base, slot);
        moveSlotMeta(base, slot, to);
      } else if (isBattery(source, record)) {
        const theirs = (stashed.get(source.db) ?? []).some((r) => r.key === record.key && isFile(source, r));
        if (!theirs && !cloudNames.has(`${base}.sav`)) continue;
        await keepBattery(record);
        await removeLive(source, [record]);
        notify(
          `${gameLabel(base)}: this account already has an in-game save, so the one from before you signed in is kept aside on this device, unsynced. Restore it from the game's Kept saves in the library.`,
        );
      }
    }
  }

  // Bookkeeping both sides have: the account's stays, the signed-out copy
  // goes aside; slot lists are merged, since their slots no longer clash.
  const liveNow = liveLocal();
  const lists = Object.entries(liveNow).filter(([key]) => key.startsWith(SLOTS));
  const clashing = Object.fromEntries(
    Object.entries(liveNow).filter(([key]) => key in theirLocal && !key.startsWith(SLOTS)),
  );
  if (Object.keys(clashing).length) {
    const earlier = (await readStash(SIGNED_OUT)).get(LOCAL_STORAGE)?.[0]?.value as Record<string, string> | undefined;
    await putStash(SIGNED_OUT, LOCAL_STORAGE, [{ key: "all", value: { ...earlier, ...clashing } }]);
  }
  await restore(sub);
  for (const [key, value] of lists) {
    const merged = new Set([...readList(key), ...(JSON.parse(value) as number[])]);
    localStorage.setItem(key, JSON.stringify([...merged].sort((a, b) => a - b)));
  }
  localStorage.setItem(OWNER_KEY, sub);
}

/** Puts the owner's waiting states into slots that have come free. */
export async function placePendingStates(
  sub: string,
  cloudNames: () => Promise<Set<string> | null>,
  maxSlot = MAX_SLOT,
) {
  const pending = await readStash(pendingProfile(sub));
  if (!pending.size) return;
  const names = await cloudNames();
  if (!names) return;
  const taken = new Slots();
  for (const name of names) taken.addState(name);
  for (const source of SOURCES)
    for (const r of await readLive(source)) if (isState(source, r)) taken.addState(fileName(r.key));
  taken.addLists(liveLocal());
  for (const source of SOURCES) {
    const records = pending.get(source.db) ?? [];
    const placed: Entry[] = [];
    for (const record of records) {
      const name = fileName(record.key);
      const base = romBaseName(name);
      const to = taken.free(base, maxSlot, new Slots());
      if (to === null) continue;
      await writeLive(source, [{ key: renamed(record.key, to), value: record.value }]);
      taken.add(base, to);
      for (const rom of romsOf(base))
        localStorage.setItem(SLOTS + rom, JSON.stringify([...readList(SLOTS + rom), to].sort((a, b) => a - b)));
      placed.push(record);
      notify(
        `${gameLabel(base)}: your state from before you signed in, which was waiting for a free slot, is now in slot ${to}.`,
      );
    }
    if (placed.length === records.length) await dropStash(pendingProfile(sub), source.db);
    else if (placed.length) {
      await dropStash(pendingProfile(sub), source.db);
      await putStash(
        pendingProfile(sub),
        source.db,
        records.filter((r) => !placed.includes(r)),
      );
    }
  }
}

/**
 * Battery saves an earlier adoption kept under SIGNED_OUT, which nothing
 * could reach, go to the signed-in account's kept saves.
 */
async function keepSignedOutBatteries(stashed: Map<string, Entry[]>) {
  for (const source of SOURCES) {
    const records = (stashed.get(source.db) ?? []).filter((r) => isBattery(source, r));
    if (!records.length) continue;
    for (const record of records) await keepBattery(record);
    await dropStash(SIGNED_OUT, source.db);
  }
}

/** Runs `work` if no other tab holds the saves; false when one does. */
async function whenAlone(work: () => Promise<void>): Promise<boolean> {
  if (!navigator.locks) {
    await work();
    return true;
  }
  return navigator.locks.request(LOCK, { mode: "exclusive", ifAvailable: true }, async (lock) => {
    if (!lock) return false;
    await work();
    return true;
  });
}

// Every tab keeps the lock shared for as long as it lives.
function holdSharedLock() {
  navigator.locks?.request(LOCK, { mode: "shared" }, () => new Promise<never>(() => {}));
}

/**
 * Makes the browser's saves the signed-in account's before anything reads
 * them, or nobody's when signed out. "blocked" means another tab is open (or
 * a swap failed) and the saves still belong to someone else; cloud sync
 * stays off for them.
 */
export interface SettleOptions {
  /** The signed-in account's cloud save names, or null when the cloud can't be reached. */
  cloudNames?: () => Promise<Set<string> | null>;
  maxSlot?: number;
}

async function hasLiveSaves(): Promise<boolean> {
  for (const source of SOURCES) if ((await readLive(source)).some((r) => isFile(source, r))) return true;
  return false;
}

export async function settleProfile(
  sub: string | null,
  onSwitching: () => void = () => {},
  { cloudNames = async () => new Set<string>(), maxSlot = MAX_SLOT }: SettleOptions = {},
): Promise<ProfileState> {
  const alone = async (work: () => Promise<void>) => {
    onSwitching();
    try {
      return await whenAlone(work);
    } catch (e) {
      console.error("Switching saves failed; it resumes at the next load.", e);
      return false;
    }
  };
  const journal = readJournal();
  if (journal && !(await alone(() => swapProfile(journal.from, journal.to, journal.phase)))) {
    holdSharedLock();
    return "blocked";
  }
  const owner = localStorage.getItem(OWNER_KEY);
  if (sub && owner && owner !== sub && !(await alone(() => swapProfile(owner, sub)))) {
    holdSharedLock();
    return "blocked";
  }
  if (!sub && owner && !(await alone(() => swapProfile(owner, null)))) {
    holdSharedLock();
    return "blocked";
  }
  // Adopting needs the account's cloud slots, so nothing is overwritten
  // there either; unreachable, it waits for the next load, ownerless.
  const adopt = async (sub: string) => {
    const names = (await hasLiveSaves()) ? await cloudNames() : new Set<string>();
    if (!names) throw new Error("The cloud's saves couldn't be listed.");
    await adoptProfile(sub, names, maxSlot);
  };
  if (sub && !owner && !(await alone(() => adopt(sub)))) {
    holdSharedLock();
    return "blocked";
  }
  if (sub && (await readStash(pendingProfile(sub))).size)
    await alone(() => placePendingStates(sub, cloudNames, maxSlot));
  const signedOut = sub ? await readStash(SIGNED_OUT) : new Map<string, Entry[]>();
  if (SOURCES.some((source) => signedOut.has(source.db))) await alone(() => keepSignedOutBatteries(signedOut));
  holdSharedLock();
  return "ready";
}
